/**
 * person-access-api.ts — client for the ADR-113 D25 person-centric access view.
 *
 *   GET /admin/people/{sub}/access
 *
 * > Staff search for a human being and see **every place that person has
 * > access**… each named properly, each changeable or removable in place.
 * > **This is the resolution of B53.**
 *
 * ⚠ THE TYPES MIRROR THE SERVER'S AND MUST NOT ADD JUDGEMENT. In particular
 * `grantsAccountReach` and `nameResolved` are SERVER-DECIDED and are rendered as
 * given. A client that recomputed either — "an advocate row obviously grants
 * nothing, I'll infer it from `kind`" — would be a second implementation of a
 * rule that already exists, free to drift from it. The server says; the screen
 * shows.
 */
import { adminApi, adminApiInZone } from './api-client';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone } from './zones';

/**
 * Route one call to the ENTRY'S own zone (ADR-122 Pattern 5). Omitting it means HOME.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

export type AccessEntryKind =
  | 'account'
  | 'consultant_connection'
  | 'partner'
  | 'operator'
  | 'advocate';

/**
 * What this screen may DO to one row (D25 — "each changeable or removable in
 * place"). Mirrors the server's `AccessAction`.
 *
 * ⛔ NEVER DERIVE ONE OF THESE FROM `kind`, `detail` OR `status`. The list is
 * server-decided for the same reason `grantsAccountReach` is: deriving it would be
 * a second implementation of an authority rule, free to drift from the first. In
 * particular `suspend` vs `reactivate` is NOT a client-side reading of `status` —
 * the server publishes the verb, and the button renders it.
 */
export type AccessAction = 'suspend' | 'reactivate' | 'remove' | 'unassign' | 'remove_grader' | 'reinstate_grader';

export interface AccessEntry {
  kind: AccessEntryKind;
  /** The underlying id — for actions. NEVER the thing shown to a human (D25). */
  id: string;
  /** What a human reads. Already an honest "Unnamed …" form when unresolved. */
  label: string;
  nameResolved: boolean;
  detail?: string;
  /** ⛔ Server-decided. False for every Advocate row (D10). Never recompute. */
  grantsAccountReach: boolean;
  /**
   * ⛔ Server-decided. What this screen may do to this row, in place. EMPTY IS A
   * REAL ANSWER — "this screen does not own this row" — and must render as the
   * absence of controls, never as a disabled control or as "actions unknown".
   */
  availableActions: AccessAction[];
  /** The operator org the write routes address. Differs from `id` on a
   *  consultant connection, where `id` is the CLIENT account. */
  orgId?: string;
  /** Lifecycle state, where the row has one. Shown, never used to pick the verb. */
  status?: 'active' | 'suspended' | 'removed';
  /** A REMOVED grader's row only (ADR-133 Amendment 2 §5): why and when staff removed the role. Server-decided. */
  graderRemoval?: { reasons: string[]; reasonLabels: string[]; note: string; removedAt: string; removedBy: string };
  /**
   * The consultant's invite code — present on exactly one entry, the consultant PROGRAMME
   * membership (server-decided, 27 September 2026). Its presence is how the screen knows the
   * person IS a consultant, independently of how many customers they have been connected to.
   */
  consultantCode?: string;

  /**
   * ADR-122 Pattern 5 — which zone this access row lives in.
   *
   * Added by the client after the person-keyed fan-out, NOT returned by the server: each
   * zone's API answers only for itself. Every write below routes back to it, because the
   * account or org the row attaches to exists in exactly one zone.
   */
  zone?: string;
}

export interface PersonAccessView {
  cognitoSub: string;
  entries: AccessEntry[];
  /** How many entries could not be named — surfaced so a regression is visible. */
  unnamedCount: number;
}

export async function fetchPersonAccess(
  cognitoSub: string,
  /** The zone to read. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<PersonAccessView> {
  return call<PersonAccessView>(zone, `/admin/people/${encodeURIComponent(cognitoSub)}/access`);
}

export interface PersonAccessFanout extends PersonAccessView {
  /** Labels of zones that did not answer. Empty on a complete read. */
  failedZones: string[];
}

/**
 * ADR-122 Pattern 5 — one PERSON's access, resolved across every zone.
 *
 * ⭐⭐ THIS IS THE ONE PLACE IN ADR-122 WHERE A SINGLE `?zone=` PARAMETER CANNOT WORK, which is
 * why it is its own pattern rather than Pattern 2. Every other record here belongs to exactly
 * one jurisdiction: a customer, a brief, a domain. A PERSON does not. The same human can hold
 * an account membership in Australia and a partner attachment in the United States, and asking
 * one zone for "everywhere this person has access" returns a confident, incomplete answer —
 * on the screen whose entire stated purpose (ADR-113 D25) is *"every place that person has
 * access"*. An access-control view that omits a jurisdiction is worse than one that omits a
 * list, because its claim is completeness.
 *
 * ⚠ ENTRIES ARE CONCATENATED, NOT DEDUPLICATED BY `{kind, id}`. An account or an org exists in
 * exactly one zone, so the same id cannot legitimately appear twice; if it ever does, that is
 * a real duplication worth SEEING rather than a merge artefact worth hiding. Hiding it would
 * turn a data-integrity problem into an invisible one.
 *
 * `unnamedCount` sums, because it is a count of rows on this screen and a headline that
 * disagreed with the rows below it would be its own defect.
 */
export async function fetchAllPersonAccess(cognitoSub: string): Promise<PersonAccessFanout> {
  const results = await fanOutZones(async (zone: PortalZone) => {
    const view = await fetchPersonAccess(cognitoSub, zone);
    return [{ zone, view }];
  });
  // No zone answered → a failed read. Absorbing it would render the person as having no
  // access anywhere, on the screen where that sentence carries the most weight.
  throwIfAllZonesFailed(results);

  const pages = results.flatMap((r) => r.items);
  return {
    cognitoSub,
    entries: pages.flatMap(({ zone, view }) =>
      view.entries.map((e) => ({ ...e, zone: zone.code })),
    ),
    unnamedCount: pages.reduce((n, { view }) => n + view.unnamedCount, 0),
    failedZones: failedZoneLabels(results),
  };
}

/**
 * D25's write half — change or remove one entry in place.
 *
 * ⛔⛔ THESE MUST BE CALLED FROM A REAL CONTROL, NOT MERELY EXIST. D25's read half
 * shipped with a `Manage` button that had no `onClick`, and every proof the build
 * ran was satisfiable without one. An exported function nothing invokes is the
 * same defect one layer down: it type-checks, it unit-tests, and no user can reach
 * it. The only proof that counts is a real staff session changing a real
 * attachment and the change surviving a reload.
 *
 * ⚠ THE ADDRESS IS `{kind}/{id}` BECAUSE THAT IS WHAT THE READ VIEW HANDED US.
 * The screen does not translate the row into an org-and-portal-type address —
 * the server does, in `person-access-write.ts`. A client that worked out the
 * portal type itself would have to guess between `beta` and `consultant`, which
 * the read view deliberately collapses into one `operator` kind.
 */
export async function changeAccessEntry(
  cognitoSub: string,
  entry: Pick<AccessEntry, 'kind' | 'id'>,
  op: 'suspend' | 'reactivate',
  /** The ENTRY'S own zone, from the fan-out tag. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<void> {
  await call<{ ok: true }>(zone,
    `/admin/people/${encodeURIComponent(cognitoSub)}/access/${encodeURIComponent(entry.kind)}/${encodeURIComponent(entry.id)}`,
    { method: 'PATCH', body: JSON.stringify({ op }) },
  );
}

export async function removeAccessEntry(
  cognitoSub: string,
  entry: Pick<AccessEntry, 'kind' | 'id'>,
  /** The ENTRY'S own zone, from the fan-out tag. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<void> {
  await call<{ ok: true }>(zone,
    `/admin/people/${encodeURIComponent(cognitoSub)}/access/${encodeURIComponent(entry.kind)}/${encodeURIComponent(entry.id)}`,
    { method: 'DELETE' },
  );
}

/**
 * Unassign this consultant from one subscriber.
 *
 * ⚠ `orgId` IS REQUIRED AND THE SERVER REFUSES WITHOUT IT. A consultant
 * connection is keyed `CASSIGN#<orgId>#<sub>` inside the CLIENT's partition, so
 * the client account alone does not name the row. The read view supplies it on
 * every `consultant_connection` entry precisely so this call can be made from the
 * row the staff member is looking at.
 */
export async function unassignFromSubscriber(
  cognitoSub: string,
  clientAccountId: string,
  orgId: string,
  /** The CLIENT account's zone — the assignment row lives in its partition. */
  zone?: PortalZone,
): Promise<void> {
  await call<{ ok: true }>(zone,
    `/admin/people/${encodeURIComponent(cognitoSub)}/access/consultant-assignments/${encodeURIComponent(clientAccountId)}` +
      `?orgId=${encodeURIComponent(orgId)}`,
    { method: 'DELETE' },
  );
}

/**
 * The operator org types a staff member may appoint into.
 *
 * ⛔⛔ THERE IS NO `advocate` MEMBER, AND ADDING ONE WOULD BE A REAL DEFECT.
 * Advocates self-register through the AOW (D5) — there is no admin-creatable
 * path and there must not be one, because an Advocate org carries a legal entity
 * and a payout rail that the org's own people declare. The union is closed here
 * so a picker cannot offer the option by accident; `user-drilldown-access-merge.test.ts`
 * asserts the absence rather than trusting this comment.
 */
export type AppointPortalType = 'consultant' | 'beta' | 'partner';

/**
 * D25's APPOINT half — *"Appointing a consultant and assigning them to
 * subscribers happens here."*
 *
 * ⚠ THE IDENTITY IS RESOLVED BY EMAIL, NOT BY `{sub}`. `addPortalOperator`
 * reuses an existing subscriber identity when the email matches and mints one
 * when it does not (ADR-081 — never a second identity on a second attachment).
 * `{sub}` names the person whose screen this is, and the server refuses with
 * `email_belongs_to_another_person` if the two disagree — so an appointment can
 * never silently land on someone else while a staff member watches this page.
 */
export async function appointOperator(
  cognitoSub: string,
  /** `jurisdiction` — ADR-124 D4: the appointee's own country, required for a consultant. */
  body: { portalType: AppointPortalType; role: string; email: string; orgId?: string; jurisdiction?: string },
  /**
   * The ORG's zone when appointing into an existing one. Omitted = the home zone, which is
   * also the correct destination when `orgId` is absent: the appointment mints a new org, and
   * a record that does not exist yet has no zone to be routed to — it is born where the
   * operator's console is.
   */
  zone?: PortalZone,
): Promise<void> {
  await call<{ ok: true }>(zone,
    `/admin/people/${encodeURIComponent(cognitoSub)}/access`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/**
 * Assign this consultant to one subscriber account.
 *
 * ⚠ THE SERVER MAY REFUSE, AND THE REFUSAL IS THE VALUABLE PART. `409
 * client_disassociated` means the CUSTOMER closed this relationship; only they
 * can re-open it. Writing the assignment anyway would create a `CASSIGN#` row
 * that looks like access and reaches nothing — the exact "row that looks like
 * access and is not" this guard exists to prevent. Callers must surface it.
 */
export async function assignToSubscriber(
  cognitoSub: string,
  clientAccountId: string,
  orgId: string,
  /** The CLIENT account's zone — the `CASSIGN#` row lands in its partition. */
  zone?: PortalZone,
): Promise<void> {
  await call<{ ok: true }>(zone,
    `/admin/people/${encodeURIComponent(cognitoSub)}/access/consultant-assignments/${encodeURIComponent(clientAccountId)}`,
    { method: 'PUT', body: JSON.stringify({ orgId }) },
  );
}

/** Human labels for the action buttons. Exhaustive by type, like KIND_LABELS. */
export const ACTION_LABELS: Record<AccessAction, string> = {
  suspend: 'Suspend',
  reactivate: 'Reactivate',
  remove: 'Remove',
  unassign: 'Unassign',
  remove_grader: 'Remove',
  reinstate_grader: 'Reinstate',
};

/**
 * ⚠ WHICH ACTIONS NEED CONFIRMING. Suspension is reversible from this same row
 * (the server publishes `reactivate` on the next read); removal and unassignment
 * are not — the attachment row is deleted and the consent grant may flip with it.
 * An irreversible act on an access-control screen gets a confirm step.
 */
export const DESTRUCTIVE_ACTIONS: ReadonlySet<AccessAction> = new Set(['remove', 'unassign', 'remove_grader']);

/**
 * Human labels for the group headings. Kept beside the type so a new kind cannot
 * be added without a label being chosen for it — `Record<AccessEntryKind, …>` is
 * exhaustive, so the compiler refuses a missing entry.
 *
 * ⚠ THAT PROTECTION DOES NOT EXTEND TO `KIND_ORDER` in `PersonAccessPanel.tsx`,
 * which is a plain array and therefore compiles happily while missing a kind —
 * and a kind absent from it is silently dropped from the screen. Adding a kind
 * here is caught by the compiler; adding it there is on you. Extend both.
 */
export const KIND_LABELS: Record<AccessEntryKind, string> = {
  account: 'Accounts',
  consultant_connection: 'Consultant connections',
  partner: 'Partner organisations',
  operator: 'Operator programmes',
  advocate: 'Advocate organisations',
};

// ── ADR-133 Amendment 2 §5 — the Grader row: removal WITH A REASON, and Reinstate (COMMUNITY build 3, Stage B) ──

/** The preset reasons — the server's keys (`lambdas/account/src/grader-role.ts` GRADER_REMOVAL_REASONS), in order. */
export const GRADER_REMOVAL_REASONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'timing', label: 'Answers faster than a person can read the page' },
  { key: 'uniform', label: 'The same timing on every page' },
  { key: 'out_of_step', label: 'Consistently out of step with other graders' },
  { key: 'honeypot', label: 'Filled in the hidden field' },
  { key: 'conduct', label: 'Breached the Grader Policy' },
  { key: 'request', label: 'Asked to leave grading' },
];

/** A removal needs at least one preset ticked, or a note of three characters or more. PURE. */
export function removalReasonValid(reasons: readonly string[], note: string): boolean {
  return reasons.length > 0 || note.trim().length >= 3;
}

/** The reason as one line — the preset labels then the note — for the community re-check. PURE. */
export function removalReasonLine(reasons: readonly string[], note: string): string {
  const labels = GRADER_REMOVAL_REASONS.filter((r) => reasons.includes(r.key)).map((r) => r.label);
  if (note.trim()) labels.push(note.trim());
  return labels.join('; ');
}

/** POST /admin/people/{sub}/access/grader/remove — the role goes, and a record bars re-joining by training. */
export async function removeGraderRole(cognitoSub: string, body: { reasons: string[]; note: string }, zone?: PortalZone): Promise<void> {
  await call<{ removed: true }>(zone,
    `/admin/people/${encodeURIComponent(cognitoSub)}/access/grader/remove`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/** POST /admin/people/{sub}/access/grader/reinstate — the role back with the pool it had; the bar lifted. */
export async function reinstateGraderRole(cognitoSub: string, note: string, zone?: PortalZone): Promise<void> {
  await call<{ reinstated: true }>(zone,
    `/admin/people/${encodeURIComponent(cognitoSub)}/access/grader/reinstate`,
    { method: 'POST', body: JSON.stringify({ note }) },
  );
}

/**
 * The community service's half (S9): after the role is removed, every grade the grader gave is quarantined
 * ("grader removed") and the results they touched are recomputed. A separate call because the grades are S9's
 * records and the role is S1's — the community service checks the attachment really is gone before acting.
 */
export async function recheckRemovedGrader(cognitoSub: string, reason: string, zone?: PortalZone): Promise<void> {
  await call<{ quarantined: number }>(zone,
    `/admin/grading/graders/${encodeURIComponent(cognitoSub)}/removed`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

// ── User Management › Advocates (29 October 2026) ────────────────────────────────────────────

/** One Advocate org as `GET /admin/advocates` returns it — the org and its people. */
export interface AdvocateOrgRow {
  orgId: string;
  name: string;
  slug: string;
  /** D15 — how the org came to exist (the public AOW, or a subscriber's account menu). Immutable. */
  origin: 'public' | 'subscriber';
  status: 'active' | 'suspended';
  /** The legal entity type is declared at payout setup (D16): true once the Advocate has entered payout details. */
  payoutDetails: boolean;
  createdAt: string | null;
  members: Array<{ cognitoSub: string; email: string; displayName: string | null; addedAt: string | null }>;
}

/** Every Advocate org in ONE zone. Read-only — staff never attach an Advocate (D5); Detach is `removeAccessEntry`. */
export async function listAdvocates(zone?: PortalZone): Promise<AdvocateOrgRow[]> {
  return (await call<{ advocates: AdvocateOrgRow[] }>(zone, '/admin/advocates')).advocates;
}

/** The Grader org — one singleton per zone (`lambdas/account/src/operator-orgs.ts`, ADR-133 Amendment 2 §1). */
export const GRADER_ORG_ID = 'nexa-grader-programme';
